/**
 * @param {String} language - the language of the desired text
 * @param {Object} feature - the feature to generate a label for
 * @param {Function} getTemplateString - a function to use for getting template string
 * @returns {String} - the complete feature label, format: "{label} {location type}"
 */
function getFeatureLabel( language, feature ) {
    let labels;

    if ( typeof feature.properties.labels === 'string' ) {
        try {
            labels = JSON.parse( feature.properties.labels );
        } catch ( err ) {
            throw new Error( err.message + ', labels: "' + feature.properties.labels + '", location: ' + feature.properties.type + ':' + feature.properties.id );
        }
    } else {
        labels = feature.properties.labels;
    }

    return labels[ language ] || labels[ 'es' ];
}

/**
 * @param {String} language - the language of the desired text
 * @param {Object} feature - the feature to generate a label for
 * @param {Function} getTemplateString - a function to use for getting template string
 * @returns {String} - the complete feature label, format: "{label} {location type} ({number})"
 */
function getFeatureNameWithPropertyCount( language, feature, getTemplateString ) {
    let labelTxt = getFeatureLabel( language, feature, getTemplateString );

    if ( feature.properties.totalProperties ) {
        labelTxt += ' (' + feature.properties.totalProperties + ')';
    }

    return labelTxt;
}

function isFeatureClickable() {
    return true;
}

/**
 * whenFeaturesAvailable resolves a Promise with an Array of map features. This allows you
 * to wait for map features after adding sources/layers
 * @param {mapboxgl.Map} ctx - a map instance
 * @param {Object} options - options to pass to ctx.querySourceFeatures
 * @returns a Promise that will resolve with an array of map features
 * @example
 * whenFeaturesAvailable( map, {
 *  source: 'sourceName',
 *  filter: null,
 *  validate: false
 * } ).then( function( features ) {
 *  // use the features here
 * } )
 */
function whenFeaturesAvailable( ctx, options, abortSignal ) {
    const source = options.source;
    let resolved = false;

    return new Promise( ( resolve, reject ) => {
        const onsourcedata = () => {
            if ( abortSignal && abortSignal.aborted ) {
                ctx.off( 'idle', onsourcedata );
                reject( new DOMException( 'AbortError' ) );
            }

            if ( !ctx.getSource( source ) || !ctx.isSourceLoaded( source ) || !ctx.getLayer( source + ':fill' ) || resolved ) {
                return;
            }

            const src = ctx.getSource( source );

            const srcData = src.serialize();

            ctx.off( 'idle', onsourcedata );

            resolved = true;

            resolve( srcData.data.features );
        };

        ctx.on( 'idle', onsourcedata );
    } );
}

function whenFeatureAvailable( ctx, options, abortSignal ) {
    const {
        source,
        featureID,
        validate,
    } = options;

    let resolved = false;

    return new Promise( ( resolve ) => {
        const onsourcedata = () => {
            if ( abortSignal && abortSignal.aborted ) {
                ctx.off( 'idle', onsourcedata );
                resolve( [] );
            }

            if ( !ctx.getSource( source ) || !ctx.isSourceLoaded( source ) || !ctx.getLayer( source + ':fill' ) || resolved ) {
                return;
            }

            const features = ctx.querySourceFeatures( source, {
                filter: [
                    '==', [ 'get', 'id' ], featureID,
                ],
                validate: validate,
            } );

            if ( features.length === 0 ) {
                return;
            }

            ctx.off( 'idle', onsourcedata );

            resolve( features[ 0 ] );
        };

        ctx.on( 'idle', onsourcedata );
    } );
}

function getSourceFeatures( ctx, source ) {
    const src = ctx.getSource( source );

    if ( !src ) {
        return [];
    }

    return src.serialize().data.features;
}

function copyAllFeatureState( ctx ) {
    const customSources = [
        'region',
        'province',
        'municipality',
        'district',
        'neighbourhood',
        'entity',
    ];

    const featureStateBySource = {
        'region': {},
        'province': {},
        'municipality': {},
        'district': {},
        'neighbourhood': {},
        'entity': {},
    };

    for ( let s = 0; s < customSources.length; s++ ) {
        const src = ctx.getSource( customSources[ s ] );

        if ( !src ) {
            continue;
        }

        const srcData = src.serialize();

        if ( !srcData ) {
            continue;
        }

        const features = srcData.data.features;

        if ( !features ) {
            continue;
        }

        for ( let f = 0; f < features.length; f++ ) {
            const featureState = ctx.getFeatureState( {
                id: features[ f ].id,
                source: customSources[ s ],
            } );

            if ( Object.keys( featureState ).length === 0 ) {
                continue;
            }

            featureStateBySource[ customSources[ s ] ][ features[ f ].id ] = featureState;
        }
    }

    return featureStateBySource;
}

function applyFeatureStates( ctx, states ) {
    if ( Object.keys( states ).length === 0 ) {
        return;
    }

    for ( const sourceName in states ) {
        if ( Object.keys( states[ sourceName ] ).length === 0 ) {
            continue;
        }

        const src = ctx.getSource( sourceName );

        if ( !src ) {
            continue;
        }

        for ( const featureID in states[ sourceName ] ) {
            ctx.setFeatureState( {
                id: featureID,
                source: sourceName,
            }, states[ sourceName ][ featureID ] );
        }
    }
}

function setFeatureStateForSources( ctx, sources, state ) {
    for ( let i = 0; i < sources.length; i++ ) {
        const features = ctx.querySourceFeatures( sources[ i ], {
            filter: null,
            validate: ctx.debug,
        } );

        for ( let j = 0; j < features.length; j++ ) {
            ctx.setFeatureState( {
                source: sources[ i ],
                id: features[ j ].id,
            }, state );
        }
    }
}

export {
    getFeatureLabel,
    getFeatureNameWithPropertyCount,
    isFeatureClickable,
    whenFeaturesAvailable,
    whenFeatureAvailable,
    copyAllFeatureState,
    applyFeatureStates,
    setFeatureStateForSources,
    getSourceFeatures,
};
